/**
 * GID-218: keep a dropdown menu inside the task pane.
 *
 * Both composer menus anchor the same way in CSS -- above the toggle, left
 * edges aligned -- which is right until the pane gets small. The task pane IS
 * the viewport here and the user can drag it to any size, so both axes need
 * checking on every open: a long option ("Loop/Harness (test)", a full model
 * id) can be wider than a narrow pane, and a short pane may not have room
 * above the control for seven options.
 *
 * The two flips are expressed as classes the stylesheet owns, so the CSS keeps
 * the actual offsets and this file only decides which way to go.
 */

// Breathing room between the menu and the edge of the pane.
const EDGE_GAP = 10;
// Below this the list is useless anyway; better to scroll a stub than to show
// a sliver, so a pane too short for either side still gets something usable.
const MIN_MENU_HEIGHT = 96;

/**
 * Measure an open menu and flip it back inside the pane.
 *
 * Call this while the menu is visible -- it reads laid-out geometry. Safe to
 * call repeatedly: it clears its own previous decisions first. In jsdom every
 * rect is zero, which reads as "no viewport" and leaves the defaults alone.
 *
 * @param {HTMLElement|null} toggle  the button the menu hangs off
 * @param {HTMLElement|null} menu    the menu itself, already shown
 */
export function fitMenuToViewport(toggle, menu) {
  if (!toggle || !menu) return;

  menu.classList.remove("is-right-aligned", "is-below");
  menu.style.maxHeight = "";
  menu.style.maxWidth = "";

  const root = menu.ownerDocument.documentElement;
  const viewportWidth = root.clientWidth;
  const viewportHeight = root.clientHeight;
  if (!viewportWidth || !viewportHeight) return;

  if (menu.getBoundingClientRect().right > viewportWidth) {
    menu.classList.add("is-right-aligned");
    // Right-aligning a menu wider than the pane pushes it off the LEFT edge,
    // which is worse: the start of every label is the part that goes missing.
    if (menu.getBoundingClientRect().left < 0) {
      menu.classList.remove("is-right-aligned");
    }
  }

  // A menu wider than the pane itself overhangs whichever edge it is pinned
  // to, so neither alignment saves it: cut it to the room actually left from
  // where it starts. Labels are ellipsized rather than clipped mid-glyph, and
  // each option carries its full text as a tooltip.
  const placed = menu.getBoundingClientRect();
  if (placed.right > viewportWidth) {
    menu.style.maxWidth = `${Math.max(0, viewportWidth - placed.left - EDGE_GAP)}px`;
  }

  const anchor = toggle.getBoundingClientRect();
  const spaceAbove = anchor.top - EDGE_GAP;
  const spaceBelow = viewportHeight - anchor.bottom - EDGE_GAP;
  const needed = menu.getBoundingClientRect().height;

  // Above by default: these controls sit at the bottom of the pane. Flip only
  // when the menu does not fit there AND there is genuinely more room below.
  const openBelow = needed > spaceAbove && spaceBelow > spaceAbove;
  if (openBelow) menu.classList.add("is-below");

  // Whichever side it lands on, never let the list run past the pane edge --
  // options past the edge would be unreachable, since the pane does not scroll.
  const available = openBelow ? spaceBelow : spaceAbove;
  if (needed > available) {
    menu.style.maxHeight = `${Math.max(MIN_MENU_HEIGHT, available)}px`;
  }
}
